import React from 'react';
import { MarketWeatherContext } from '../../types';
import { fmtPlainDate, fmtMonthYear } from '../../utils/plainDate';

interface MarketWeatherHeaderProps {
  marketWeather: MarketWeatherContext | null;
  isLoading: boolean;
}

/**
 * Market facts for the analysis page (Oct 2026). Replaces the Golden / Standard /
 * Caution / Dead "zones": no verdicts, just the numbers and how they compare with
 * the liquidity rule the screener uses (backend liquidity_gate.py). Never blocks
 * the contracts below. Gold phase 2b: IV rank / IV30 / liquidity / trend /
 * earnings days all come from gold via the backend; this component formats only.
 */
const MarketWeatherHeader: React.FC<MarketWeatherHeaderProps> = ({ marketWeather: mw, isLoading }) => {
  if (isLoading) {
    return <div className="mfacts mfacts-loading">Loading market data…</div>;
  }
  if (!mw) return null;

  const liq = mw.liquidity;
  const rule = liq?.rule;
  const spread = liq?.spread_pct ?? null;
  const reasons = new Set(liq?.reasons ?? []);
  const misses: string[] = [];
  if (liq && rule) {
    if (reasons.has('spread_above_max') && spread != null) misses.push(`spread ${spread.toFixed(1)}% is above ${rule.max_spread_pct}%`);
    if (reasons.has('spread_unknown')) misses.push(reasons.has('oi_unknown') ? 'there is no standard contract (spread and open interest unknown)' : 'the spread is unknown (no quoted standard contract yet)');
    if (reasons.has('oi_below_min') && liq.open_interest != null) misses.push(`open interest of ${liq.open_interest} is below ${rule.min_open_interest}`);
    if (reasons.has('oi_unknown') && !reasons.has('spread_unknown')) misses.push('open interest is unknown');
    if (reasons.has('volume_below_min')) misses.push(`share volume is below ${rule.min_stock_volume.toLocaleString()}`);
    if (reasons.has('volume_unknown')) misses.push('share volume is unknown');
  }
  const ivRankLabel = mw.iv_rank_interim && mw.iv_rank_since ? `IV rank (since ${fmtMonthYear(mw.iv_rank_since)})` : 'IV rank';
  const earnDate = fmtPlainDate(mw.next_earnings_date);
  const earnDays = mw.days_to_earnings;

  return (
    <div className="mfacts">
      <dl className="mfacts-grid">
        <div className="mfacts-item">
          <dt>Price</dt>
          <dd>{mw.current_price != null ? `$${mw.current_price.toFixed(2)}` : '—'}</dd>
        </div>
        <div className="mfacts-item">
          <dt title={mw.iv_rank_interim ? 'Interim: the rank uses the history available so far (under a year).' : undefined}>{ivRankLabel}</dt>
          <dd>
            {mw.iv_rank != null ? Math.round(mw.iv_rank) : '—'}
            {mw.iv != null && <span className="mfacts-sub" title="The stock's 30-day implied volatility (IV30)"> · IV {Math.round(mw.iv)}%</span>}
          </dd>
        </div>
        <div className="mfacts-item">
          <dt>Option spread</dt>
          <dd className={liq && !liq.spread_ok ? 'is-out' : ''}>
            {spread != null ? `${spread.toFixed(1)}%` : '—'}
            {rule && <span className="mfacts-sub"> · rule ≤ {rule.max_spread_pct}%</span>}
          </dd>
        </div>
        <div className="mfacts-item">
          <dt>Open interest</dt>
          <dd className={liq && !liq.open_interest_ok ? 'is-out' : ''}>
            {liq?.open_interest != null ? liq.open_interest.toLocaleString() : '—'}
            {rule && <span className="mfacts-sub"> · rule ≥ {rule.min_open_interest}</span>}
          </dd>
        </div>
        <div className="mfacts-item">
          <dt>Trend</dt>
          <dd style={{ textTransform: 'capitalize' }}>{mw.trend_signal || '—'}</dd>
        </div>
        <div className="mfacts-item">
          <dt>Next earnings</dt>
          <dd>{earnDate ? <>{earnDate}{earnDays != null && earnDays >= 0 && <span className="mfacts-sub"> · {earnDays}d</span>}</> : '—'}</dd>
        </div>
      </dl>
      {misses.length > 0 && (
        <p className="mfacts-note" role="status">
          Outside the liquidity rule: {misses.join(', and ')}. Contracts are still listed; fills can land far from
          the mid price.
        </p>
      )}
    </div>
  );
};

export default MarketWeatherHeader;
